/* eslint-disable react/prop-types */
import { useState, useCallback, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
  getSortedRowModel,
} from '@tanstack/react-table';
import { IconPencil, IconTrash, IconEye } from '@tabler/icons-react';
import format from 'date-fns/format';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { TableRowActions } from 'ui';

import { parseDonationAmount } from '../../core/utils';
import ConfirmDeleteModal from '../modals/ConfirmDeleteModal';
import CustomPagination from '../tables/CustomPagination';
import DesignationModal from './DesignationModal';

const ACTIONS = {
  viewDonations: 'View Donations',
  edit: 'Edit',
  delete: 'Delete',
};

const getActionIcon = (action) => {
  switch (action) {
    case ACTIONS.viewDonations:
      return <IconEye size={20} />;
    case ACTIONS.edit:
      return <IconPencil size={20} />;
    case ACTIONS.delete:
      return <IconTrash size={20} className="text-red-500" />;
    default:
      return null;
  }
};

const allActions = [ACTIONS.viewDonations, ACTIONS.edit, ACTIONS.delete];

const DesignationsTable = ({ data, onEdit, onDelete, loading }) => {
  const [deleteData, setDeleteData] = useState(null);
  const [editDesignation, setEditDesignation] = useState(null);
  const navigate = useNavigate();

  const handleAction = useCallback(
    (designation, action) => {
      switch (action) {
        case ACTIONS.edit:
          setEditDesignation(designation);
          break;
        case ACTIONS.delete:
          setDeleteData({ id: designation.designationId, name: designation.name, entity: 'designation' });
          break;
        case ACTIONS.viewDonations:
          navigate('/donations', { state: { designationId: designation.designationId } });
          break;
        default:
          break;
      }
    },
    [navigate],
  );

  const handleDelete = () => {
    if (!deleteData?.id) return;
    onDelete(deleteData.id);
    setDeleteData(null);
  };

  const handleEdit = ({ name, code }) => {
    if (!editDesignation) return;
    onEdit(editDesignation.designationId, { name, code });
    setEditDesignation(null);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: () => 'Name',
        footer: (props) => props.column.id,
        minSize: 200,
      },
      {
        accessorKey: 'code',
        header: () => 'Code',
        footer: (props) => props.column.id,
        cell: (info) => info.getValue() || '—',
        minSize: 150,
      },
      {
        id: 'totalDonations',
        header: () => 'Amount Raised',
        footer: (props) => props.column.id,
        accessorFn: (row) => row.totalDonations || 0,
        cell: (info) => parseDonationAmount(info.getValue() / 100),
        minSize: 150,
      },
      {
        accessorKey: 'createdAt',
        header: () => 'Created',
        footer: (props) => props.column.id,
        cell: (info) => format(new Date(info.getValue() * 1000), 'd MMM yyyy, h:mm a'),
        minSize: 180,
      },
      {
        accessorKey: 'designationId',
        header: () => '',
        footer: (props) => props.column.id,
        cell: (info) => (
          <TableRowActions
            actions={allActions}
            onSelectAction={(action) => handleAction(info.row.original, action)}
            getActionIcon={getActionIcon}
          />
        ),
        size: 86,
        minSize: 86,
      },
    ],
    [handleAction],
  );

  const table = useReactTable({
    data,
    columns,
    defaultColumn: {
      size: 'fit-content',
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      sorting: [
        {
          id: 'name',
          desc: false,
        },
      ],
    },
  });

  const emptyRows = 5 - data.length;

  return (
    <div className="space-y-6">
      <div className="relative overflow-x-auto sm:rounded-lg">
        <table className="w-full text-left text-base text-gray-950 dark:text-gray-400">
          <thead className="bg-white-100 text-sm uppercase text-gray-400 dark:bg-gray-700 dark:text-gray-400">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className="px-4 py-3"
                      style={{ width: header.column.columnDef.size, minWidth: header.column.columnDef.minSize }}
                    >
                      {header.isPlaceholder ? null : (
                        <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <tr
                  key={row.id}
                  className="h-[52px] border-b border-gray-50 bg-white-100 hover:bg-gray-10 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-600"
                >
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td
                        key={cell.id}
                        className="px-4 py-2"
                        style={{ width: cell.column.columnDef.size, minWidth: cell.column.columnDef.minSize }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {emptyRows > 0 &&
              Array(emptyRows)
                .fill('')
                .map((row, index) => (
                  <tr
                    key={`emptyRow-${index}`}
                    className="h-[52px] border-b border-gray-50 bg-white-100 hover:bg-gray-10 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-600"
                  >
                    <td className="px-4 py-2" colSpan={15} style={{ minWidth: 110 }}></td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      <CustomPagination table={table} total={data.length} />

      <ConfirmDeleteModal
        data={deleteData}
        onClose={() => setDeleteData(null)}
        onDelete={handleDelete}
        loading={loading}
      />

      {editDesignation && (
        <DesignationModal
          open
          onClose={() => setEditDesignation(null)}
          initialName={editDesignation.name}
          initialCode={editDesignation.code || ''}
          onSave={handleEdit}
          title="Edit Designation"
        />
      )}
    </div>
  );
};

DesignationsTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

DesignationsTable.defaultProps = {
  loading: false,
};

export default DesignationsTable;
